import { 
  IbkrAccountSummary, 
  IbkrAccountSummaryHistory, 
  IbkrPosition, 
  IbkrPositionHistory,
  IbkrPositionV2,
  IbkrPositionV2History
} from "../types";
import { DEFAULT_ACCOUNT_DATA_MAX_AGE_MS } from "../config/accountDataConfig";
import httpService from "./httpService";

export type AdminAccountInfoOwner = Readonly<{ userId: string }>;

export class AdminAccountInfoVerificationError extends Error {
  constructor() {
    super("Customer View could not verify this resource.");
    this.name = "AdminAccountInfoVerificationError";
  }
}

class AccountSummaryService {
  /**
   * Fetch account summary for the authenticated user.
   * @param maxAgeMs Maximum age in milliseconds before data is considered stale (default: 10 minutes, DEFAULT_ACCOUNT_DATA_MAX_AGE_MS)
   * @param signal Optional cancellation signal
   */
  public async getAccountSummary(
    maxAgeMs: number = DEFAULT_ACCOUNT_DATA_MAX_AGE_MS,
    signal?: AbortSignal
  ): Promise<IbkrAccountSummary> {
    const params = new URLSearchParams();
    params.append("maxAgeMs", maxAgeMs.toString());

    const response = await httpService.post<IbkrAccountSummary>(
      `/v2/account/summary?${params.toString()}`,
      undefined,
      { signal }
    );
    return response.data;
  }

  /**
   * Fetch account summary for a specific user (Admin only).
   * @param userId The target user's UUID
   * @param maxAgeMs Maximum age in milliseconds before data is considered stale (default: 10 minutes, DEFAULT_ACCOUNT_DATA_MAX_AGE_MS)
   * @param bypassIsCustomer Whether to bypass the iscustomer gate for the onboarding preflight
   * @param signal Optional cancellation signal
   */
  public async getAdminUserAccountSummary(
    userId: string,
    maxAgeMs: number = DEFAULT_ACCOUNT_DATA_MAX_AGE_MS,
    bypassIsCustomer: boolean = false,
    signal?: AbortSignal
  ): Promise<IbkrAccountSummary> {
    const params = new URLSearchParams();
    params.append("maxAgeMs", maxAgeMs.toString());
    if (bypassIsCustomer) {
      params.append("bypassIsCustomer", "true");
    }

    const response = await httpService.post<IbkrAccountSummary>(
      `/v2/admin/user-accounts/${userId}/summary?${params.toString()}`,
      undefined,
      { signal, requestScope: "actor" }
    );
    return response.data;
  }

  /**
   * Get account summary history for a specific user (Admin only).
   * @param userId The target user's UUID
   * @param limit Maximum number of history records to return
   */
  public async getAdminUserAccountSummaryHistory(
    userId: string,
    limit: number = 10
  ): Promise<{ history: IbkrAccountSummaryHistory[]; count: number }> {
    const params = new URLSearchParams();
    params.append("limit", limit.toString());

    const response = await httpService.get<{
      history: IbkrAccountSummaryHistory[];
      count: number;
    }>(`/v2/admin/user-accounts/${userId}/summary/history?${params.toString()}`);
    return response.data;
  }

  /**
   * Delete account summary for a specific user (Admin only).
   * @param userId The target user's UUID
   */
  public async deleteAdminUserAccountSummary(
    userId: string
  ): Promise<{ message: string; deletedCount: number }> {
    const response = await httpService.delete<{
      message: string;
      deletedCount: number;
    }>(`/v2/admin/user-accounts/${userId}/summary`);
    return response.data;
  }

  /**
   * Fetch account positions for the authenticated user.
   * @param maxAgeMs Maximum age in milliseconds before data is considered stale (default: 10 minutes, DEFAULT_ACCOUNT_DATA_MAX_AGE_MS)
   */
  public async getPositions(maxAgeMs: number = DEFAULT_ACCOUNT_DATA_MAX_AGE_MS): Promise<IbkrPosition[]> {
    const params = new URLSearchParams();
    params.append("maxAgeMs", maxAgeMs.toString());

    const url = `/v2/account/positions?${params.toString()}`;
    const response = await httpService.post<IbkrPosition[]>(url);
    return response.data;
  }

  /**
   * Fetch account positions for a specific user (Admin only).
   * @param userId The target user's UUID
   * @param maxAgeMs Maximum age in milliseconds before data is considered stale (default: 10 minutes, DEFAULT_ACCOUNT_DATA_MAX_AGE_MS)
   */
  public async getAdminUserPositions(
    userId: string,
    maxAgeMs: number = DEFAULT_ACCOUNT_DATA_MAX_AGE_MS
  ): Promise<IbkrPosition[]> {
    const params = new URLSearchParams();
    params.append("maxAgeMs", maxAgeMs.toString());

    const url = `/v2/admin/user-accounts/${userId}/positions?${params.toString()}`;
    const response = await httpService.post<IbkrPosition[]>(url);
    return response.data;
  }

  /**
   * Get position history snapshots for a specific user (Admin only).
   * @param userId The target user's UUID
   * @param limit Maximum number of history records to return (default: 10)
   */
  public async getAdminUserPositionsHistory(
    userId: string,
    limit?: number
  ): Promise<{ history: IbkrPositionHistory[]; count: number }> {
    const params = new URLSearchParams();
    if (limit !== undefined) {
      params.append("limit", limit.toString());
    }

    const queryString = params.toString();
    const url = queryString
      ? `/v2/admin/user-accounts/${userId}/positions/history?${queryString}`
      : `/v2/admin/user-accounts/${userId}/positions/history`;

    const response = await httpService.get<{ history: IbkrPositionHistory[]; count: number }>(url);
    return response.data;
  }

  // ============================================
  // V2 Positions Methods
  // ============================================

  /**
   * Fetch account positions V2 for the authenticated user.
   * @param maxAgeMs Maximum age in milliseconds before data is considered stale (default: 10 minutes, DEFAULT_ACCOUNT_DATA_MAX_AGE_MS)
   * @param signal Optional cancellation signal
   */
  public async getPositionsV2(
    maxAgeMs: number = DEFAULT_ACCOUNT_DATA_MAX_AGE_MS,
    signal?: AbortSignal
  ): Promise<IbkrPositionV2[]> {
    const params = new URLSearchParams();
    params.append("maxAgeMs", maxAgeMs.toString());

    const url = `/v2/account/positionsV2?${params.toString()}`;
    const response = await httpService.get<IbkrPositionV2[]>(url, { signal });
    return response.data;
  }

  /**
   * Fetch account positions V2 for a specific user (Admin only).
   * @param userId The target user's UUID
   * @param maxAgeMs Maximum age in milliseconds before data is considered stale (default: 10 minutes, DEFAULT_ACCOUNT_DATA_MAX_AGE_MS)
   * @param signal Optional cancellation signal
   */
  public async getAdminUserPositionsV2(
    userId: string,
    maxAgeMs: number = DEFAULT_ACCOUNT_DATA_MAX_AGE_MS,
    signal?: AbortSignal
  ): Promise<IbkrPositionV2[]> {
    const params = new URLSearchParams();
    params.append("maxAgeMs", maxAgeMs.toString());

    const url = `/v2/admin/user-accounts/${userId}/positionsV2?${params.toString()}`;
    const response = await httpService.post<IbkrPositionV2[]>(url, undefined, {
      signal,
      requestScope: "actor",
    });
    return response.data;
  }

  /**
   * Get position V2 history snapshots for a specific user (Admin only).
   * @param userId The target user's UUID
   * @param limit Maximum number of history records to return (default: 10)
   * @param signal Optional cancellation signal
   */
  public async getAdminUserPositionsV2History(
    userId: string,
    limit?: number,
    signal?: AbortSignal
  ): Promise<{ history: IbkrPositionV2History[]; count: number }> {
    const params = new URLSearchParams();
    if (limit !== undefined) {
      params.append("limit", limit.toString());
    }

    const queryString = params.toString();
    const url = queryString
      ? `/v2/admin/user-accounts/${userId}/positionsV2/history?${queryString}`
      : `/v2/admin/user-accounts/${userId}/positionsV2/history`;

    const response = await httpService.get<{
      history: IbkrPositionV2History[];
      count: number;
    }>(url, { signal, requestScope: "actor" });
    return response.data;
  }

  /**
   * Delete all account positions V2 for a specific user (Admin only).
   * @param userId The target user's UUID
   */
  public async deleteAdminUserPositionsV2(
    userId: string
  ): Promise<{ message: string; deletedCount: number }> {
    const response = await httpService.delete<{
      message: string;
      deletedCount: number;
    }>(`/v2/admin/user-accounts/${userId}/positionsV2`);
    return response.data;
  }

  /**
   * Copy only the owner userId from GET /v2/admin/user-accounts/:userId/info.
   * The raw body is dropped immediately after the copy.
   */
  public async getAdminUserAccountInfoOwner(
    userId: string
  ): Promise<AdminAccountInfoOwner> {
    const response = await httpService.get<unknown>(
      `/v2/admin/user-accounts/${userId}/info`
    );
    const raw = response.data;
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
      throw new AdminAccountInfoVerificationError();
    }
    const ownerId = (raw as { userId?: unknown }).userId;
    if (typeof ownerId !== "string") {
      throw new AdminAccountInfoVerificationError();
    }
    return { userId: ownerId };
  }
}

export const accountSummaryService = new AccountSummaryService();
export default accountSummaryService;
